import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  CalendarBlank,
  ChatCircle,
  ClipboardText,
  MapTrifold,
  Receipt,
  Gear,
  User,
  Cube,
  Plus,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { photoSrc } from "@/components/AppHeader";
import QuickActionSheet from "@/pages/mobile/QuickActionSheet";

const LEFT_TABS = [
  { to: "/mobile/day", label: "Tasks", icon: ClipboardText, id: "day" },
  { to: "/mobile/calendar", label: "Schedule", icon: CalendarBlank, id: "calendar" },
];
const RIGHT_TABS = [
  { to: "/mobile/map", label: "Map", icon: MapTrifold, id: "map" },
  { to: "/mobile/stock", label: "Stock", icon: Cube, id: "stock" },
];

export default function MobileShell() {
  const { user } = useAuth();
  const [logo, setLogo] = useState(null);
  const [name, setName] = useState("Brioni Granite");
  const [unread, setUnread] = useState(0);
  const [fabOpen, setFabOpen] = useState(false);

  useEffect(() => {
    api
      .get("/settings/branding")
      .then(({ data }) => {
        setLogo(data.header_logo || data.logo);
        if (data.company_name || data.name) setName(data.company_name || data.name);
      })
      .catch(() => setLogo(null));
  }, []);

  useEffect(() => {
    let active = true;
    const load = () =>
      api
        .get("/sms/unread")
        .then(({ data }) => active && setUnread(data.count ?? (data.items || []).length))
        .catch(() => {});
    load();
    const timer = setInterval(load, 45000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const headerBtn = (isActive) =>
    `relative flex h-9 w-9 items-center justify-center rounded-full border transition-all duration-200 ${
      isActive
        ? "border-[#c39e6d]/40 bg-[#c39e6d]/15 text-[#c39e6d]"
        : "border-white/5 bg-white/[0.03] text-slate-400 hover:bg-white/[0.07] hover:text-[#c39e6d]"
    }`;

  const tabClass = ({ isActive }) =>
    `relative flex flex-1 flex-col items-center gap-1 rounded-2xl py-1.5 text-[10px] font-bold uppercase tracking-wider transition-all duration-200 ${
      isActive ? "text-[#c39e6d]" : "text-slate-500 hover:text-slate-300"
    }`;

  const renderTab = (t) => (
    <NavLink key={t.id} to={t.to} data-testid={`mobile-tab-${t.id}`} className={tabClass}>
      {({ isActive }) => (
        <>
          <t.icon size={22} weight={isActive ? "fill" : "bold"} />
          {t.label}
        </>
      )}
    </NavLink>
  );

  return (
    <div className="grain min-h-screen bg-[#080C14]" data-testid="mobile-shell">
      <div className="mx-auto flex min-h-screen w-full max-w-md flex-col">
        <header
          className="no-print sticky top-0 z-40 flex h-16 items-center gap-2.5 border-b border-white/[0.06] bg-[#080C14]/80 px-4 backdrop-blur-xl"
          data-testid="mobile-topbar"
        >
          {logo ? (
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/public/header-logo`}
              alt="Logo"
              className="h-9 w-9 shrink-0 rounded-xl border border-slate-800 object-cover"
            />
          ) : (
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#c39e6d]/40 bg-[#c39e6d]/15 text-[#c39e6d]">
              <Cube size={19} weight="bold" />
            </span>
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-bold uppercase tracking-[0.1em] text-slate-100">
              {name}
            </div>
            {(user?.name || user?.email) && (
              <div className="truncate text-[10px] font-semibold uppercase tracking-widest text-[#c39e6d]">
                {user.name || user.email}
              </div>
            )}
          </div>
          <NavLink
            to="/mobile/messages"
            data-testid="mobile-messages-link"
            className={({ isActive }) => headerBtn(isActive)}
            title="Messages"
            aria-label="Messages"
          >
            <ChatCircle size={17} weight="bold" />
            {unread > 0 && (
              <span
                data-testid="mobile-header-unread"
                className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white ring-2 ring-[#080C14]"
              >
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </NavLink>
          <NavLink to="/mobile/bills" data-testid="mobile-bills-link" className={({ isActive }) => headerBtn(isActive)} title="Bills" aria-label="Bills">
            <Receipt size={17} weight="bold" />
          </NavLink>
          <NavLink to="/mobile/accounts" data-testid="mobile-accounts-link" className={({ isActive }) => headerBtn(isActive)} title="Account" aria-label="Account">
            <User size={17} weight="bold" />
          </NavLink>
          <NavLink to="/mobile/settings" data-testid="mobile-settings-link" className={({ isActive }) => headerBtn(isActive)} title="Settings" aria-label="Settings">
            <Gear size={17} weight="bold" />
          </NavLink>
        </header>

        <main className="relative flex-1 overflow-y-auto pb-28">
          <Outlet />
        </main>
      </div>

      <QuickActionSheet open={fabOpen} onOpenChange={setFabOpen} />

      <nav
        className="no-print fixed bottom-3 left-1/2 z-50 flex w-[calc(100%-1.5rem)] max-w-md -translate-x-1/2 items-center justify-between gap-1 rounded-full border border-slate-700/60 bg-[#0F172A]/90 px-3 pb-[max(0.35rem,env(safe-area-inset-bottom))] pt-1.5 shadow-2xl shadow-black/80 backdrop-blur-xl"
        data-testid="mobile-bottom-nav"
      >
        {LEFT_TABS.map(renderTab)}
        <button
          data-testid="mobile-center-fab"
          aria-label="Quick actions"
          onClick={() => setFabOpen(true)}
          className="relative -top-5 flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#c39e6d] text-slate-950 shadow-[0_0_25px_rgba(195,158,109,0.5)] transition-transform active:scale-90"
        >
          <Plus size={28} weight="bold" />
        </button>
        {RIGHT_TABS.map(renderTab)}
      </nav>
    </div>
  );
}
